<template>
  <section
    class="standings-round-details-section"
    :class="{ 'standings-round-details-section--is-open': open }"
  >
    <round-details-header-parts
      class="standings-round-details-section__header"
      :raceData="roundRecord.race"
    />

    <div class="standings-round-details-section__earn-points">
      <dl class="standings-round-details-section__earn-points__data">
        <dt
          class="standings-round-details-section__earn-points__label"
          v-text="$tc('StandingsPage.DetailsPage.EarnPoints')"
        />
        <dd
          class="standings-round-details-section__earn-points__value"
          v-text="`${roundRecord.earnPoints} pt`"
        />
      </dl>
    </div>

    <div class="standings-round-details-section__list-heading">
      <span v-text="$tc('StandingsPage.DetailsPage.RaceRank')" />
    </div>

    <div class="standings-round-details-section__list">
      <round-record-parts
        class="standings-round-details-section__list-item"
        v-for="(record, index) in roundRecord.records"
        :key="`roundRecordParts${index}`"
        :title="record.title"
        :value="record.record"
        :position="record.position"
      />
    </div>

    <button
      class="standings-round-details-section__toggle-button"
      type="button"
      @click="toggleOpen"
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M16.1001 6.03906L10.2769 11.8623C9.59346 12.5457 8.48542 12.5457 7.802 11.8623L1.97878 6.03906H4.1001L8.86266 10.8016C8.96029 10.8993 9.11858 10.8993 9.21621 10.8016L13.9788 6.03906H16.1001Z"
          fill="black"
        />
      </svg>
    </button>
  </section>
</template>

<script lang="ts">
import { defineComponent, PropType, ref } from '@vue/composition-api'
import RoundDetailsHeaderParts, {
  RoundDetailsHeaderDataType,
} from '@/components/StandingsPage/StandingsRoundDataPane/parts/RoundDetailsHeaderParts.vue'
import RoundRecordParts, {
  RoundRecordDataType,
} from '@/components/StandingsPage/StandingsRoundDataPane/parts/RoundRecordParts.vue'

export type StandingsRoundDataType = {
  earnPoints: number
  race: RoundDetailsHeaderDataType
  records: RoundRecordDataType[]
}

/**
 * Standings: レース詳細 セクション
 */
export default defineComponent({
  name: 'StandingsRoundDetailsSection',
  components: {
    RoundDetailsHeaderParts,
    RoundRecordParts,
  },
  props: {
    roundRecord: {
      type: Object as PropType<StandingsRoundDataType>,
      required: true,
    },
  },
  setup() {
    const open = ref<boolean>(false)
    const toggleOpen = () => {
      open.value = !open.value
    }
    return {
      open,
      toggleOpen,
    }
  },
  methods: {},
})
</script>

<style lang="scss" scoped>
.standings-round-details-section {
  position: relative;
  width: 100%;
  max-height: min(#{convertPXtoVW(336), 336px});
  padding-bottom: min(#{convertPXtoVW(50), 50px});
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 16px;
  background: $white;
  overflow: hidden;
  transition: max-height ease 0.4s;

  &--is-open {
    max-height: 1200px;

    .standings-round-details-section__toggle-button svg {
      transform: rotate(180deg);
    }
  }

  &__earn-points {
    width: 100%;
    padding: min(#{convertPXtoVW(16), 16px});

    &__data {
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding: min(#{convertPXtoVW(5), 5px}) min(#{convertPXtoVW(12), 12px});
      letter-spacing: 0.02em;
      color: $black;
      background: $black05;
    }

    &__label {
      font-weight: 600;
      font-size: min(#{convertPXtoVW(12), 12px});
      line-height: 1.67;
    }

    &__value {
      font-weight: 500;
      font-size: min(#{convertPXtoVW(16), 16px});
      line-height: 1.38;
    }
  }

  &__list {
    width: 100%;
    border-top: 1px solid $black05;

    &-heading {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      padding: min(#{convertPXtoVW(7), 7px}) min(#{convertPXtoVW(28), 28px})
        min(#{convertPXtoVW(6), 6px}) min(#{convertPXtoVW(20), 20px});
      font-weight: 600;
      font-size: min(#{convertPXtoVW(12), 12px});
      line-height: 1.33;
      letter-spacing: 0.04em;
      color: $gray25;
      border-top: 1px solid $black05;
    }

    &-item {
      border-bottom: 1px solid $black05;
    }
  }

  &__toggle-button {
    position: absolute;
    left: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: min(#{convertPXtoVW(50), 50px});
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.8) 0%, #ffffff 100%);
  }
}
</style>
